import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useEffect } from "react";
import { Grid } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { AuthStore } from "stores/AuthStore";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import { useRestrictedOptions } from "../BaseFilterComponents/useRestrictedOptions";

const DEFAULT_ALLOWED_MODES = ["include", "exclude"] as const;

export const TopicsFilter = observer(function (
    props: IApplicationFilterComponent,
) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    const canViewTopics = authStore.canUserView("Topics");
    const isTopicsLoading = valuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_TOPICS,
    );
    const { availableOptions: availableTopics } = useRestrictedOptions({
        options: valuesStore.topicClusters,
        getOptionValue: (topic) => topic.id,
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });
    const allowedModes =
        props.modeRestriction?.allowedModes ?? DEFAULT_ALLOWED_MODES;
    const modeOptions: Array<{
        label: string;
        value: boolean;
        isDisabled?: boolean;
    }> = [
        {
            label: "is one of",
            value: true,
            isDisabled: !allowedModes.includes("include"),
        },
        {
            label: "is not one of",
            value: false,
            isDisabled: !allowedModes.includes("exclude"),
        },
    ];

    useEffect(() => {
        if (!props.modeRestriction) return;
        const currentMode = props.store.containsTopics ? "include" : "exclude";
        if (allowedModes.includes(currentMode)) return;
        props.store.setContainsTopics(allowedModes[0] === "include");
    }, [allowedModes, props.modeRestriction, props.store]);

    useEffect(() => {
        if (!canViewTopics) return;

        void valuesStore.loadTopics();
    }, [canViewTopics, valuesStore]);

    if (!canViewTopics) return null;

    return (
        <FilterSectionContainer>
            <Grid container item direction="column">
                <Grid item>
                    <AcxSelectSingle
                        id={"application-filters-themes-contains"}
                        options={modeOptions}
                        labelField="label"
                        valueField="value"
                        defaultValue={{
                            label: props.store.containsTopicsString,
                            value: props.store.containsTopics,
                        }}
                        customStyle={customFilterSelectStyles}
                        onChange={(item) => {
                            if (item.isDisabled) return;
                            props.store.setContainsTopics(item.value);
                        }}
                        isDisabled={
                            modeOptions.filter((item) => !item.isDisabled)
                                .length <= 1
                        }
                    />
                </Grid>
                <Grid item>
                    <AcxSelectMulti
                        id="application-filters-themes"
                        fullWidth
                        isLoading={isTopicsLoading}
                        isDisabled={isTopicsLoading}
                        options={availableTopics}
                        defaultValue={props.store.topics}
                        onChange={(options) => {
                            props.store.setTopics(!!options ? options : []);
                        }}
                        valueField="id"
                        labelField="topicLabel"
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                </Grid>
            </Grid>
        </FilterSectionContainer>
    );
});
